import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DatePicker from 'react-datepicker';

// Helper function to get all dates between a start and end date
const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
    // Ensure the loop includes the endDate
    currentDate.setUTCHours(0, 0, 0, 0);
    const finalDate = new Date(endDate);
    finalDate.setUTCHours(0, 0, 0, 0);

    while (currentDate <= finalDate) {
        dates.push(currentDate.toISOString().split('T')[0]); // Format as YYYY-MM-DD
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
};

const PerformanceChart = ({ milkRecords, healthRecords, feedRecords }) => {
    // Default to the last 7 days
    const [endDate, setEndDate] = useState(new Date());
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 6);
        return date;
    });

    // This hook now recalculates the chart data whenever the date range changes
    const chartData = useMemo(() => {
        if (!startDate || !endDate) return [];

        const dates = getDatesInRange(startDate, endDate);
        
        return dates.map(date => {
            const dailyMilk = milkRecords
                .filter(r => r.date === date)
                .reduce((sum, r) => sum + (r.morningYield || 0) + (r.eveningYield || 0), 0);
            const dailyHealthCost = healthRecords
                .filter(r => r.date === date)
                .reduce((sum, r) => sum + (Number(r.cost) || 0), 0);
            const dailyFeedCost = feedRecords
                .filter(r => r.date === date)
                .reduce((sum, r) => sum + (r.cost || 0), 0);

            return {
                name: new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }),
                milk: dailyMilk.toFixed(1),
                healthCost: dailyHealthCost.toFixed(2),
                feedCost: dailyFeedCost.toFixed(2),
            };
        });
    }, [milkRecords, healthRecords, feedRecords, startDate, endDate]);

    // --- NEW: Handler with validation logic ---
    const handleStartDateChange = (date) => {
        setStartDate(date);
        // If the new start date is after the current end date,
        // update the end date to match the new start date.
        if (date > endDate) {
            setEndDate(date);
        }
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 sm:mb-0">Performance Overview</h3>
                <div className="flex items-center space-x-2">
                    <div>
                        <label htmlFor="startDate" className="text-sm font-medium text-gray-700 mr-2">From:</label>
                        <DatePicker
                            selected={startDate}
                            onChange={handleStartDateChange} // Use the new handler
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            className="form-input rounded-md shadow-sm w-32"
                            portalId="portal-root"
                        />
                    </div>
                    <div>
                        <label htmlFor="endDate" className="text-sm font-medium text-gray-700 mr-2">To:</label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate} // Prevents selecting an end date before the start date
                            className="form-input rounded-md shadow-sm w-32"
                            portalId="portal-root"
                        />
                    </div>
                </div>
            </div>

            <div style={{ height: '400px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                        <YAxis yAxisId="left" label={{ value: 'Liters (L)', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" label={{ value: 'Cost (₹)', angle: -90, position: 'insideRight' }} />
                        <Tooltip formatter={(value, name) => [`${value} ${name === 'milk' ? 'L' : '₹'}`, name.replace('Cost', ' Cost')]} />
                        <Legend verticalAlign="top" />
                        <Line yAxisId="left" type="monotone" dataKey="milk" stroke="#3b82f6" strokeWidth={2} name="Milk Yield" dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="healthCost" stroke="#ef4444" strokeWidth={2} name="Health Cost" dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="feedCost" stroke="#f97316" strokeWidth={2} name="Feed Cost" dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default PerformanceChart;